import { effect, inject, Injectable, Injector, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AuthService } from '@services/auth';
import { Routing } from '@enums/routing.enum';
import { NativePushService } from './native-push.service';

/**
 * One-time native (Capacitor) shell setup: status-bar style, Android hardware-back handling, and the
 * mobile entry route. Everything is guarded by `Capacitor.isNativePlatform()`, so on the WEB this is
 * a complete no-op — the browser build stays behaviourally identical.
 *
 * NOTE: the launch splash is NOT hidden here on purpose — the Capacitor SplashScreen plugin
 * auto-hides it after `launchShowDuration` (capacitor.config.ts). Hiding it manually right after
 * bootstrap was cutting off the full logo+wordmark splash before it could show.
 *
 * Called once from `AppComponent`'s constructor. See docs/mobile/CAPACITOR_PLAN.md Faza 4.
 */
@Injectable({ providedIn: 'root' })
export class NativeService {
	private readonly _router = inject(Router);
	private readonly _auth = inject(AuthService);
	private readonly _push = inject(NativePushService);
	private readonly _injector = inject(Injector);

	async init(): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return; // web → nothing to do
		}

		// Dark status bar to match the app's dark theme; solid background on Android.
		try {
			await StatusBar.setStyle({ style: Style.Dark });

			if (Capacitor.getPlatform() === 'android') {
				await StatusBar.setBackgroundColor({ color: '#0a0e14' });
			}
		} catch {
			/* status bar is non-critical */
		}

		// Mobile entry route: the app must NOT open on the public marketing home ('') with the CMS
		// shell/nav flashing before login. Send the user straight to dashboard (if a session exists)
		// or login. Only when we're actually at the root path.
		if (window.location.pathname === '/' || window.location.pathname === '') {
			const target = this._auth.isAuthenticated()
				? Routing.PanelDashboard
				: Routing.PanelLogin;
			void this._router.navigateByUrl(`/${target}`, { replaceUrl: true });
		}

		// Phone notifications (FCM): register the device for whoever is logged in, unlink it on logout.
		try {
			await this._push.init();
			this._auth.onBeforeLogout(() => this._push.forgetDevice());
			effect(
				() => {
					if (this._auth.isAuthenticated()) {
						untracked((): void => void this._push.onSession());
					}
				},
				{ injector: this._injector },
			);
		} catch (e) {
			console.warn('[FCM] init failed', e);
		}

		// Android hardware BACK: go back within the app history; exit only at the root screen.
		await App.addListener('backButton', ({ canGoBack }) => {
			if (canGoBack) {
				window.history.back();
			} else {
				void App.exitApp();
			}
		});
	}
}
